import { useRef, useState } from "react";

import type { LabelField, LabelSettings } from "../types";
import { calculateBarcodeWidthForDPI } from "../utils/barcodeUtils";
import {
  calculateTextDisplayDimensions,
  getTextLinesForPreview,
} from "../utils/textRenderer";

interface LabelCanvasProps {
  settings: LabelSettings;
  fields: LabelField[];
  selectedField: string | null;
  onSelectField: (id: string | null) => void;
  onUpdateField: (id: string, updates: Partial<LabelField>) => void;
  onDeleteField: (id: string) => void;
}

export function LabelCanvas({
  settings,
  fields,
  selectedField,
  onSelectField,
  onUpdateField,
  onDeleteField,
}: LabelCanvasProps) {
  const canvasRef = useRef<HTMLDivElement>(null);
  const [dragState, setDragState] = useState<{
    fieldId: string;
    startX: number;
    startY: number;
    offsetX: number;
    offsetY: number;
  } | null>(null);

  // Calculate scale to fit within available space while maintaining aspect ratio
  const maxDisplayWidth = 600;
  const maxDisplayHeight = 400;
  const labelAspectRatio = settings.width / settings.height;

  let canvasWidth: number;
  let canvasHeight: number;
  let scale: number;

  // Determine if we should scale based on width or height constraint
  if (labelAspectRatio > maxDisplayWidth / maxDisplayHeight) {
    // Label is wider relative to available space - constrain by width
    canvasWidth = maxDisplayWidth;
    canvasHeight = maxDisplayWidth / labelAspectRatio;
    scale = maxDisplayWidth / settings.width;
  } else {
    // Label is taller relative to available space - constrain by height
    canvasHeight = maxDisplayHeight;
    canvasWidth = maxDisplayHeight * labelAspectRatio;
    scale = maxDisplayHeight / settings.height;
  }

  const handleMouseDown = (e: React.MouseEvent, field: LabelField) => {
    e.preventDefault();
    e.stopPropagation();

    onSelectField(field.id);

    const rect = canvasRef.current?.getBoundingClientRect();
    if (!rect) return;

    const fieldElement = e.currentTarget as HTMLElement;
    const fieldRect = fieldElement.getBoundingClientRect();

    setDragState({
      fieldId: field.id,
      startX: e.clientX,
      startY: e.clientY,
      offsetX: e.clientX - fieldRect.left,
      offsetY: e.clientY - fieldRect.top,
    });
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!dragState || !canvasRef.current) return;

    const rect = canvasRef.current.getBoundingClientRect();
    const marginLeft = settings.marginLeft || 8;
    const marginTop = settings.marginTop || 8;
    const marginRight = settings.marginRight || 8;
    const marginBottom = settings.marginBottom || 8;

    // Find the field being dragged to get its actual dimensions
    const draggedField = fields.find((f) => f.id === dragState.fieldId);
    if (!draggedField) return;

    // Calculate actual field dimensions
    let fieldWidth = draggedField.width;
    let fieldHeight = draggedField.height;

    if (draggedField.type === "barcode") {
      const calculation = calculateBarcodeWidthForDPI(
        draggedField.content,
        settings.dpi,
        0.0075, // min X dimension
        draggedField.barcodeHeight || 50,
        draggedField.showText ?? true,
        12, // font size for text below barcode
      );
      fieldWidth = calculation.widthInDots;
      fieldHeight = calculation.heightInDots;
    } else if (draggedField.type === "text") {
      const dimensions = calculateTextDisplayDimensions(draggedField, settings);
      fieldWidth = dimensions.width;
      fieldHeight = dimensions.height;
    }

    // Constrain to printable area (within margins) accounting for actual field size
    const minX = marginLeft;
    const maxX = settings.width - marginRight - fieldWidth;
    const minY = marginTop;
    const maxY = settings.height - marginBottom - fieldHeight;

    const newX = Math.max(
      minX,
      Math.min((e.clientX - rect.left - dragState.offsetX) / scale, maxX),
    );
    const newY = Math.max(
      minY,
      Math.min((e.clientY - rect.top - dragState.offsetY) / scale, maxY),
    );

    onUpdateField(dragState.fieldId, {
      x: Math.round(newX),
      y: Math.round(newY),
    });
  };

  const handleMouseUp = () => {
    setDragState(null);
  };

  const handleCanvasClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onSelectField(null);
    }
  };

  const handleCanvasKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") {
      onSelectField(null);
    }
  };

  return (
    <div className="flex flex-col h-full">
      <div className="flex-1 flex items-center justify-center p-4 bg-gray-50">
        <div
          ref={canvasRef}
          className="relative bg-white shadow-lg flex-shrink-0 rounded-lg"
          style={{
            width: canvasWidth,
            height: canvasHeight,
            minWidth: canvasWidth,
            minHeight: canvasHeight,
            maxWidth: canvasWidth,
            maxHeight: canvasHeight,
          }}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
          onClick={handleCanvasClick}
          onKeyDown={handleCanvasKeyDown}
          role="button"
          tabIndex={0}
          aria-label="Label canvas - click to deselect fields, press Escape to deselect"
        >
          {/* Grid overlay */}
          <div
            className="absolute inset-0 opacity-20 pointer-events-none"
            style={{
              backgroundImage: `
                linear-gradient(to right, #ccc 1px, transparent 1px),
                linear-gradient(to bottom, #ccc 1px, transparent 1px)
              `,
              backgroundSize: `${20 * scale}px ${20 * scale}px`,
            }}
          />

          {/* Printable area indicator - using SVG for precise positioning */}
          <svg
            className="absolute pointer-events-none"
            aria-label="Printable area"
            style={{
              left: 0,
              top: 0,
              width: canvasWidth,
              height: canvasHeight,
            }}
          >
            <title>Printable area</title>
            {/* Main printable area border */}
            <rect
              x={(settings.marginLeft || 8) * scale}
              y={(settings.marginTop || 8) * scale}
              width={
                (settings.width -
                  (settings.marginLeft || 8) -
                  (settings.marginRight || 8)) *
                scale
              }
              height={
                (settings.height -
                  (settings.marginTop || 8) -
                  (settings.marginBottom || 8)) *
                scale
              }
              rx="4"
              ry="4"
              fill="none"
              stroke="#6b7280"
              strokeWidth="2"
              strokeDasharray="6 4"
            />
          </svg>

          {/* Render fields */}
          {fields.map((field) => (
            <FieldElement
              key={field.id}
              field={field}
              scale={scale}
              isSelected={selectedField === field.id}
              onMouseDown={(e) => handleMouseDown(e, field)}
              onDelete={() => onDeleteField(field.id)}
              settings={settings}
            />
          ))}

          {/* Label dimensions indicator */}
          <div className="absolute -top-6 left-0 text-xs text-gray-500">
            {settings.width} × {settings.height} dots
          </div>
        </div>
      </div>
    </div>
  );
}

interface FieldElementProps {
  field: LabelField;
  scale: number;
  isSelected: boolean;
  onMouseDown: (e: React.MouseEvent) => void;
  onDelete: () => void;
  settings: LabelSettings;
}

function FieldElement({
  field,
  scale,
  isSelected,
  onMouseDown,
  onDelete,
  settings,
}: FieldElementProps) {
  // Calculate accurate dimensions for fields
  let displayWidth = field.width;
  let displayHeight = field.height;

  if (field.type === "barcode") {
    const calculation = calculateBarcodeWidthForDPI(
      field.content,
      settings.dpi,
      0.0075, // min X dimension
      field.barcodeHeight || 50,
      field.showText ?? true,
      12, // font size for text below barcode
    );
    displayWidth = calculation.widthInDots;
    displayHeight = calculation.heightInDots;
  } else if (field.type === "text") {
    const dimensions = calculateTextDisplayDimensions(field, settings);
    displayWidth = dimensions.width;
    displayHeight = dimensions.height;
  }

  const style = {
    position: "absolute" as const,
    left: field.x * scale,
    top: field.y * scale,
    width: displayWidth * scale,
    height: displayHeight * scale,
    border: isSelected ? "2px solid #3b82f6" : "1px solid #d1d5db",
    backgroundColor: isSelected
      ? "rgba(59, 130, 246, 0.1)"
      : "rgba(255, 255, 255, 0.8)",
    cursor: "move",
    display: "flex",
    alignItems: "flex-start",
    padding: "2px 4px",
    fontSize: Math.max(6, (field.fontSize || 20) * scale * 0.8),
    fontFamily: "monospace",
    overflow: "visible",
    userSelect: "none" as const,
  };

  return (
    <div style={style} onMouseDown={onMouseDown} className="group">
      {field.type === "text" ? (
        <div
          className={`w-full h-full overflow-hidden leading-none ${
            field.alignment === "C"
              ? "text-center"
              : field.alignment === "R"
                ? "text-right"
                : "text-left"
          }`}
          style={{ overflow: "hidden" }}
        >
          {(() => {
            const lines = getTextLinesForPreview(field, settings);
            return lines.map((line, index) => (
              <div key={index} className="whitespace-nowrap">
                {line}
              </div>
            ));
          })()}
        </div>
      ) : (
        <div className="flex flex-col items-center w-full h-full">
          <div
            className="bg-black"
            style={{
              width: "100%",
              height: (field.barcodeHeight || 50) * scale,
              backgroundImage:
                "repeating-linear-gradient(90deg, black 0px, black 1px, white 1px, white 2px)",
            }}
          />
          {field.showText && (
            <div
              className="flex items-center justify-center flex-1 w-full"
              style={{ fontSize: Math.max(6, 12 * scale) }}
            >
              <span className="truncate text-center">{field.content}</span>
            </div>
          )}
        </div>
      )}

      {isSelected && (
        <button
          type="button"
          className="absolute top-1 right-1 w-4 h-4 bg-red-500 hover:bg-red-600 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center text-xs font-bold"
          onClick={(e) => {
            e.stopPropagation();
            onDelete();
          }}
          title="Delete field"
        >
          ×
        </button>
      )}
    </div>
  );
}
